import { ExternalLink, Calendar, MapPin, Building2, Ban, Check, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalOpportunity } from '../../types/opportunity.types';
import { useImportCartStore } from '../../stores/import-cart.store';

interface ExternalOpportunityCardProps {
  opportunity: ExternalOpportunity;
  onExclude?: (opportunity: ExternalOpportunity) => void;
}

export function ExternalOpportunityCard({
  opportunity,
  onExclude,
}: ExternalOpportunityCardProps) {
  const { addItem, removeItem, isInCart } = useImportCartStore();
  const inCart = isInCart(opportunity.externalId);
  const isDisabled = opportunity.isImported || opportunity.isExcluded;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Non spécifiée';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatAmount = (min?: number, max?: number) => {
    if (!min && !max) return null;
    const formatter = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    });
    if (min && max) {
      return `${formatter.format(min)} - ${formatter.format(max)}`;
    }
    if (min) return `À partir de ${formatter.format(min)}`;
    if (max) return `Jusqu'à ${formatter.format(max)}`;
    return null;
  };

  const getMarketTypeLabel = (type?: string) => {
    switch (type) {
      case 'services':
        return 'Services';
      case 'supplies':
        return 'Fournitures';
      case 'works':
        return 'Travaux';
      default:
        return null;
    }
  };

  const handleCartToggle = () => {
    if (inCart) {
      removeItem(opportunity.externalId);
    } else {
      addItem(opportunity);
    }
  };

  return (
    <Card
      className={`transition-all ${
        isDisabled
          ? 'opacity-60 bg-muted'
          : 'hover:shadow-md hover:border-copper/50'
      }`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge
                variant="outline"
                className={`text-xs shrink-0 ${
                  opportunity.source === 'ted'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-copper text-copper'
                }`}
              >
                {opportunity.source === 'ted' ? 'TED' : 'BOAMP'}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {opportunity.externalId}
              </span>
              {opportunity.isImported && (
                <Badge variant="secondary" className="text-xs">
                  Déjà importé
                </Badge>
              )}
              {opportunity.isExcluded && (
                <Badge variant="destructive" className="text-xs">
                  Exclu
                </Badge>
              )}
              {opportunity.noticeNature === 'AVIS_RECTIFICATIF' && (
                <Badge variant="outline" className="text-xs border-amber-500 bg-amber-50 text-amber-700">
                  Rectificatif
                </Badge>
              )}
            </div>
            <CardTitle className="text-base leading-tight line-clamp-2">
              {opportunity.title}
            </CardTitle>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Acheteur */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Building2 className="w-4 h-4 shrink-0" />
          <span className="truncate">{opportunity.buyerName}</span>
        </div>

        {/* Infos ligne */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {opportunity.department && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <MapPin className="w-4 h-4" />
              <span>
                {opportunity.source === 'ted'
                  ? opportunity.department
                  : `Dép. ${opportunity.department}`}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1 text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>Limite: {formatDate(opportunity.submissionDeadline)}</span>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          {getMarketTypeLabel(opportunity.marketType) && (
            <Badge variant="secondary">
              {getMarketTypeLabel(opportunity.marketType)}
            </Badge>
          )}
          {formatAmount(opportunity.amountMin, opportunity.amountMax) && (
            <Badge variant="outline">
              {formatAmount(opportunity.amountMin, opportunity.amountMax)}
            </Badge>
          )}
          {opportunity.isMultiLot && opportunity.lotsCount && (
            <Badge variant="outline">{opportunity.lotsCount} lots</Badge>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            asChild
          >
            <a
              href={opportunity.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              Voir l'avis
            </a>
          </Button>

          {!isDisabled && (
            <>
              <Button
                variant={inCart ? 'secondary' : 'default'}
                size="sm"
                className={`text-xs ml-auto ${!inCart ? 'bg-copper hover:bg-copper/90' : ''}`}
                onClick={handleCartToggle}
              >
                {inCart ? (
                  <>
                    <Check className="w-3 h-3 mr-1" />
                    Dans le panier
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-3 h-3 mr-1" />
                    Ajouter
                  </>
                )}
              </Button>

              {onExclude && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-destructive hover:text-destructive"
                  onClick={() => onExclude(opportunity)}
                >
                  <Ban className="w-3 h-3 mr-1" />
                  Exclure
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
